import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { AsyncValidatorFn, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { MessageService } from '@shared/services/message-service';
import { DateTime } from 'luxon';
import { finalize, Observable, map, EMPTY } from "rxjs";
import { TrailerService } from '../../../../services/trailer.service';
import { Trailer } from '../../../../models/trailer';

@Component({
    selector: 'app-trailer-number-admin-input',
    templateUrl: './trailer-number-admin-input.component.html',
    styleUrls: ['./trailer-number-admin-input.component.scss']
})
export class TrailerNumberAdminInputComponent implements OnInit {
    @Input() trailer: Trailer;
    @Input() shiftDate: DateTime;
    @Output() onError = new EventEmitter<void>();
    @Output() onUpdate = new EventEmitter<Trailer>();

    formControl = new FormControl<string>('', [ Validators.required, Validators.maxLength(50) ]);

    updating: boolean = false;

    constructor(private trailerService: TrailerService, private messageService: MessageService) {}

    ngOnInit(): void {
        this.formControl.setValue(this.trailer.trailerNumber);
        this.formControl.addAsyncValidators([ this.generateDupeTrailerNumberValidator() ]);
    }

    generateDupeTrailerNumberValidator(): AsyncValidatorFn {
        let initialValue = this.trailer.trailerNumber;

        return (control: FormControl<string>): Observable<ValidationErrors | null> => {
            if (!control.value || initialValue === control.value) {
                return EMPTY;
            }

            return this.trailerService.getTrailers({
                siteId: this.trailer.siteId,
                date: this.shiftDate,
                carrierId: this.trailer.carrierId,
                trailerNumber: control.value,
                hasDeparted: false,
            }).pipe(map(trailers => {
                const otherTrailers = trailers.filter(trailer => trailer.id != this.trailer.id);
                const hasDuplicateTrailer = otherTrailers.length > 0;

                if (hasDuplicateTrailer) {
                    return { duplicateDetected: { valid: false } };
                }
                
                return null;
            })) 
        }
    }

    private setUpdatingState(isUpdating: boolean): void {
        this.updating = isUpdating;

        if(isUpdating) {
            this.formControl.disable();
        } else {
            this.formControl.enable();
        }
    }

    updateTrailerNumber(): void {
        if(!this.formControl.valid) {
            return;
        }

        this.setUpdatingState(true);
        this.trailerService.updateTrailerNumber(this.trailer.id, this.formControl.value).pipe(finalize(() => {
            this.setUpdatingState(false);
        })).subscribe({
            next: (trailer) => {
                this.onUpdate.emit(trailer);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Trailer Updated',
                    detail: `Trailer ${this.trailer.trailerNumber} has been updated.`
                });
            },
            error: () => {
                this.onError.emit();
            }
        });
    }
};